import { StorageService } from '../services/StorageService.js';

/**
 * DiscoveryState
 * Encapsulates all discovery session state with auto-save
 */
export class DiscoveryState {
    constructor(storage = new StorageService()) {
        this.storage = storage;

        // Initialize state
        this._project = '';
        this._projectTitle = '';
        this._conversation = [];
        this._currentQuestion = 0;
        this._answers = {
            project: '',
            selling: '',
            businessModel: '',
            buyer: '',
            brandName: '',
            tagline: '',
            offer: '',
            uniqueValue: '',
            constraints: ''
        };
        this._aiMode = 'structured';
        this._productContext = null;
        this._businessContext = null;
        this._editMode = false;
        this._editCampaignId = null;
        this._extractedKnowledge = null;
        this._knowledgeReviewShown = false;
        this._canReturnToReview = false;

        // MP-04: Pre-fill from NEXUS org profile if available
        this._prefillFromOrgProfile();
    }

    /**
     * MP-04: Pre-fill answer fields from NEXUS org profile stored in sessionStorage.
     * Only fills empty fields — user-entered values are never overwritten.
     */
    _prefillFromOrgProfile() {
        try {
            const profileJson = sessionStorage.getItem('nexus_org_profile');
            if (!profileJson) return;

            const orgProfile = JSON.parse(profileJson);

            // Map org profile fields to discovery answers (only if currently empty)
            if (orgProfile.targetAudience && !this._answers.buyer) {
                this._answers.buyer = orgProfile.targetAudience;
            }
        } catch (e) {
            console.warn('[DiscoveryState] Failed to load org profile for pre-fill:', e.message);
        }
    }

    // Getters
    get project() { return this._project; }
    get projectTitle() { return this._projectTitle; }
    get conversation() { return this._conversation; }
    get currentQuestion() { return this._currentQuestion; }
    get answers() { return { ...this._answers }; } // Return copy
    get aiMode() { return this._aiMode; }
    get productContext() { return this._productContext; }
    get businessContext() { return this._businessContext; }
    get editMode() { return this._editMode; }
    get editCampaignId() { return this._editCampaignId; }
    get extractedKnowledge() { return this._extractedKnowledge; }
    get knowledgeReviewShown() { return this._knowledgeReviewShown; }
    get canReturnToReview() { return this._canReturnToReview; }

    // Setters (with auto-save)
    set project(value) {
        this._project = value;
        this._autoSave();
    }

    set projectTitle(value) {
        this._projectTitle = value;
        this._autoSave();
    }

    set currentQuestion(value) {
        if (value < 0) throw new Error('currentQuestion cannot be negative');
        this._currentQuestion = value;
        this._autoSave();
    }

    set aiMode(value) {
        if (!['structured', 'freeform'].includes(value)) {
            throw new Error('Invalid aiMode');
        }
        this._aiMode = value;
    }

    set editMode(value) {
        this._editMode = Boolean(value);
    }

    set editCampaignId(value) {
        this._editCampaignId = value;
    }

    set extractedKnowledge(value) {
        this._extractedKnowledge = value;
    }

    set knowledgeReviewShown(value) {
        this._knowledgeReviewShown = Boolean(value);
    }

    set canReturnToReview(value) {
        this._canReturnToReview = Boolean(value);
    }

    // Context setters
    setProductContext(context) {
        this._productContext = context;
    }

    setBusinessContext(context) {
        this._businessContext = context;
    }

    // Conversation management
    addMessage(sender, text) {
        this._conversation.push({ sender, text });
        this._autoSave();
    }

    clearConversation() {
        this._conversation = [];
        this._autoSave();
    }

    removeLastMessages(count = 2) {
        this._conversation.splice(-count, count);
        this._autoSave();
    }

    // Answer management
    setAnswer(key, value) {
        // Allow dynamic keys for intelligent interview questions
        this._answers[key] = value;
        this._autoSave();
    }

    getAnswer(key) {
        return this._answers[key];
    }

    clearAnswer(key) {
        if (this._answers.hasOwnProperty(key)) {
            this._answers[key] = '';
            this._autoSave();
        }
    }

    getAllAnswers() {
        return { ...this._answers };
    }

    // Progress tracking
    getProgress() {
        const totalQuestions = 9; // Adjust based on questions array
        return {
            current: this._currentQuestion,
            total: totalQuestions,
            percentage: Math.round((this._currentQuestion / totalQuestions) * 100)
        };
    }

    isComplete() {
        return this._currentQuestion >= 9;
    }

    // Persistence
    save() {
        const data = {
            project: this._project,
            projectTitle: this._projectTitle,
            conversation: this._conversation,
            currentQuestion: this._currentQuestion,
            answers: this._answers,
            extractedKnowledge: this._extractedKnowledge,
            knowledgeReviewShown: this._knowledgeReviewShown,
            canReturnToReview: this._canReturnToReview,
            timestamp: Date.now()
        };

        return this.storage.save('session', data);
    }

    restore() {
        const data = this.storage.load('session');
        if (!data) return false;

        this._project = data.project || '';
        this._projectTitle = data.projectTitle || '';
        this._conversation = data.conversation || [];
        this._currentQuestion = data.currentQuestion || 0;
        this._answers = { ...this._answers, ...data.answers };
        this._extractedKnowledge = data.extractedKnowledge || null;
        this._knowledgeReviewShown = data.knowledgeReviewShown || false;
        this._canReturnToReview = data.canReturnToReview || false;

        return true;
    }

    clear() {
        this.storage.remove('session');
        this.reset();
    }

    reset() {
        this._project = '';
        this._projectTitle = '';
        this._conversation = [];
        this._currentQuestion = 0;
        this._answers = {
            project: '',
            selling: '',
            businessModel: '',
            buyer: '',
            brandName: '',
            tagline: '',
            offer: '',
            uniqueValue: '',
            constraints: ''
        };
        this._extractedKnowledge = null;
        this._knowledgeReviewShown = false;
        this._canReturnToReview = false;
        this._editMode = false;
        this._editCampaignId = null;
    }

    // Private auto-save
    _autoSave() {
        this.save();
    }

    // Serialization
    toJSON() {
        return {
            project: this._project,
            projectTitle: this._projectTitle,
            currentQuestion: this._currentQuestion,
            answers: this._answers,
            timestamp: Date.now()
        };
    }

    static fromJSON(data) {
        const state = new DiscoveryState();
        state._project = data.project;
        state._projectTitle = data.projectTitle;
        state._currentQuestion = data.currentQuestion;
        state._answers = { ...state._answers, ...data.answers };
        return state;
    }
}
